function bytesToBase64(bytes: Uint8Array): string {
  const chunk = 0x8000;
  let binary = "";
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

function isZipFilename(filename: string): boolean {
  return filename.toLowerCase().endsWith(".zip");
}

function nativeFetchByUrl(filename: string): boolean {
  const lower = filename.toLowerCase();
  return isZipFilename(filename) || lower.endsWith(".mp4") || lower.endsWith(".webm");
}

function nativeSaveMime(filename: string): string {
  const lower = filename.toLowerCase();
  if (lower.endsWith(".mp4")) return "video/mp4";
  if (lower.endsWith(".webm")) return "video/webm";
  return "application/octet-stream";
}

function absoluteDownloadUrl(url: string): string {
  const root = globalThis as unknown as { window?: { location?: { href?: string } }; location?: { href?: string } };
  const href = root.window?.location?.href ?? root.location?.href;
  try {
    return href ? new URL(url, href).href : url;
  } catch {
    return url;
  }
}

function flutterHandler(): { callHandler: (name: string, ...args: unknown[]) => Promise<unknown> } | null {
  const root = globalThis as unknown as {
    flutter_inappwebview?: { callHandler?: (name: string, ...args: unknown[]) => Promise<unknown> };
    window?: { flutter_inappwebview?: { callHandler?: (name: string, ...args: unknown[]) => Promise<unknown> } };
  };
  const bridge = root.window?.flutter_inappwebview ?? root.flutter_inappwebview;
  return bridge?.callHandler ? { callHandler: bridge.callHandler.bind(bridge) } : null;
}

export async function fetchAuthenticatedBlob(url: string): Promise<Blob> {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) {
    throw new Error(res.status === 401 ? "Sign in to download this file." : `Download failed (${res.status})`);
  }
  return res.blob();
}

export async function fetchAuthenticatedText(url: string): Promise<string> {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) {
    throw new Error(res.status === 401 ? "Sign in to open this file." : `Could not load file (${res.status})`);
  }
  return res.text();
}

export async function fetchAuthenticatedBytes(url: string): Promise<Uint8Array> {
  const blob = await fetchAuthenticatedBlob(url);
  return new Uint8Array(await blob.arrayBuffer());
}

export async function saveOrDownloadFile(
  filename: string,
  blob: Blob,
): Promise<{ saved: boolean; path?: string; folder?: string }> {
  const native = flutterHandler();
  if (native) {
    const bytes = new Uint8Array(await blob.arrayBuffer());
    const result = (await native.callHandler("saveFile", {
      filename,
      mime: blob.type || "application/octet-stream",
      base64: bytesToBase64(bytes),
    })) as { ok?: boolean; path?: string; folder?: string; error?: string } | null;
    if (result?.ok) return { saved: true, path: result.path, folder: result.folder ?? "Downloads" };
    if (result?.error) throw new Error(result.error);
  }

  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 2_000);
  return { saved: false };
}

export async function downloadAuthenticated(
  url: string,
  filename: string,
): Promise<{ saved: boolean; path?: string; folder?: string }> {
  const native = flutterHandler();
  if (native && nativeFetchByUrl(filename)) {
    const result = (await native.callHandler("saveFile", {
      filename,
      mime: nativeSaveMime(filename),
      url: absoluteDownloadUrl(url),
    })) as { ok?: boolean; path?: string; folder?: string; error?: string } | null;
    if (result?.ok) return { saved: true, path: result.path, folder: result.folder ?? "Downloads" };
    if (result?.error) throw new Error(result.error);
  }
  const blob = await fetchAuthenticatedBlob(url);
  return saveOrDownloadFile(filename, blob);
}
